import React from "react"
import { connect } from "react-redux"
import { mouseTrap } from "react-mousetrap"
import tracker from "../../common/tracker"
import * as actions from "../actions"
import Search from "./Search"
import { Button } from "buoy-core"
import styled, { keyframes, css } from "styled-components"
import { responsive, colors } from "buoy-theme"
import { inIframe } from "../../common/common"

// a/b/c/d window.search_abcd_test ===
// 'common_search' to show common searches
// 'keyboard' to activate keyboard on focus, locking search to top on type
// 'question' to display symptom question as label above locked search
const isCommonSearchTest = () => {
  return window.search_abcd_test === 'common_search' ||
    window.location.search === '?abcd_search_test=common_search'
}

const isKeyboardAbTest = () => {
  return window.search_abcd_test === 'keyboard' ||
    window.location.search === '?abcd_search_test=keyboard'
}

const isSearchWithQuestionTest = () => {
  return window.search_abcd_test === 'question' ||
    window.location.search === '?abcd_search_test=question'
}

const isAutoActivateSearchTest = () => {
  return window.auto_activate_search === true ||
    window.location.search === '?auto_activate_search=true'
}

const AbTestSuggestions = [
  {
    text: 'headache',
    token: 'headache',
  },{
    text: 'abdominal pain',
    token: 'abdominal pain',
  },{
    text: 'nausea',
    token: 'nausea',
  },{
    text: 'fatigue',
    token: 'fatigue',
  },{
    text: 'cough',
    token: 'cough',
  }
]

const filterSuggestions = suggestions => {
  return suggestions.filter(
    x => x.token !== "PLACEHOLDER"
  )
}

const SearchButton = styled(Button)`
  margin-top: 0.556rem;
  ${props => !props.loading && `margin-bottom:2rem`};
`
  
const SearchOuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: none;
  width: 100%;
  opacity: 1;
  margin-top: 24px;
  padding-left: 0;
  position: relative;

  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
    -webkit-appearance: none;
  }

  > * {
    width: 100%;
    max-width: 496px;
    margin-left: auto;
    margin-right: auto;
  }

  ${props =>
    props.focused &&
    css`
      position: ${props => (props.editorActive ? "absolute" : "fixed")};
      margin: 0 auto;
      height: 100%;
      left: 0;
      right: 0;
      top: 0;
      overflow-y: scroll;
      box-sizing: border-box;
      padding: 0px 16px 80px;

      ${props =>
        props.iFrame
          ? css`
              padding-top: 16px;
            `
          : props.forCovid19
          ? css`
              padding-top: 105px;
              @media (max-width: 350px) {
                padding-top: 110px;
              }
              @media (min-width: 350px) and (max-width: 500px) {
                padding-top: 95px;
              }
              @media (min-width: 500px) and (max-width: 600px) {
                padding-top: 100px;
              }
            `
          : responsive("padding-top", "68px", "68px", "59px", "59px", "56px")}

      background: white;
      animation: ${fade} 0.4s ease;

      form {
        min-height: 48px;
        button {
          color: ${colors.neutralDarkest};
        }
      }
    `}

  form + p:nth-child(2) {
    padding-left: 16px;
    ${responsive("margin-top", "20px", "20px", "20px", "20px", "10px")}
  }

  form + div:nth-child(2),
  p + div:nth-child(3) {
    height: auto;
    box-sizing: border-box;
  }

  ${props =>
    !props.focused &&
    `
    animation: ${fade1} 0.4s ease;
  `}
`

const fade = keyframes`
  0%  { opacity: 0 }
  100%  { opacity: 1 }
`

const fade1 = keyframes`
  0%,1%  { opacity: 0 }
  100%  { opacity: 1 }
`

let searchBarClicked = false

const SearchBubble = props => (
  <SearchOuterContainer {...props} id="search-bar" iFrame={props.iFrame}>
    <Search
      {...props}
      value={props.search}
      onKeyDown={props.keyDown}
      results={props.searchResults}
      age={props.identifiers.age}
      sex={props.identifiers.sex}
      suggestionClick={props.submitSearch}
      showCancel={props.focused}
      placeholder={props.first ? "Search for a symptom" : "Add another symptom"}
      // for a/b/c/d test
      showQuestion={isSearchWithQuestionTest()}
      suggestions={filterSuggestions(props.searchSuggestions)}
      showCommonSearches={props.focused &&
        !props.searchResults.items &&
        props.search.length === 0 &&
        isCommonSearchTest() }
      autoActivateSearch={isAutoActivateSearchTest()}
    />

    {!props.first && !props.focused ? (
      <SearchButton
        {...props}
        id="close-input"
        onClick={props.startInterview}
        disabled={!!props.finishedInputting || props.loading}
      >
        I'm done adding symptoms
      </SearchButton>
    ) : null}
  </SearchOuterContainer>
)

class SearchBubbleContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      focused: false,
      locked: false, // added for keyboard activation a/b test
      scrolling: false,
    }
  }

  componentDidMount() {
    document.addEventListener("keydown", this.keyDown, false)
    document.addEventListener("scroll", this.onScroll, false)
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.keyDown, false)
    document.removeEventListener("scroll", this.onScroll, false)
    window.clearTimeout(this.scrolling)
    window.clearTimeout(this.scrollToSearchTimer)
  }

  onScroll = () => {
    if (this.state.focused && this.input) {
      this.input.blur()
    }
  }

  componentDidUpdate = (prevProps, prevState) => {
    // should only set common search suggestions if they are not already set
    if (
      isCommonSearchTest() &&
      this.props.searchSuggestions !== AbTestSuggestions
    ) {
      this.setAbTestSuggestions()
    }

    // scrolls to search input when it transitions between locked and inline-chat position
    // determined by props.locked if keyboard a/b test is active, props.focused if not active
    if (isKeyboardAbTest() || isAutoActivateSearchTest()) {
      if (this.state.locked !== prevState.locked) {
        let searchElement = this.state.locked ? "search" : "search-input"
        this.scrollToSearch(searchElement)
      }
    } else if (this.state.focused !== prevState.focused) {
      let searchElement = this.state.focused ? "search" : "search-input"
      this.scrollToSearch(searchElement)
    }
  }

  keyDown = e => {
    let key = e.keyCode || e.which
    switch (key) {
      case 27: // ESC
        e.target.blur()
        this.cancelClick()
        break
      default:
        break
    }
  }

  setListenerOnScrollFinish = () => {
    // Clear timeout until so we only add the listener once we stop scrolling
    window.clearTimeout(this.scrolling)
    this.scrolling = setTimeout(() => {
      document.addEventListener("scroll", this.onScroll, false)
      document.removeEventListener("scroll", this.setListenerOnScrollFinish, false)
    }, 66)
  }

  scrollToSearch = element => {
    document.removeEventListener("scroll", this.onScroll, false)
    this.scrollToSearchTimer = setTimeout(() => {
      document.getElementById(element).scrollIntoView({ behavior: "smooth" })
      window.addEventListener("scroll", this.setListenerOnScrollFinish, false)
    }, 20)
  }

  onFocus = e => {
    if (e.target.id === "close-input") {
      return
    }
    this.setState({ focused: true })
    this.props.searchFocus()
    this.setAbTestSuggestions()
  }

  resultClick = (token, title) => {
    this.props.submitClarifier(token, title)
    this.cancelClick()
  }

  cancelClick = () => {
    this.setState({ focused: false, locked: false })
    this.props.setInactive()
    this.props.searchClear()
  }

  searchClear = () => {
    let suggestions = isCommonSearchTest() ? AbTestSuggestions : []
    this.props.searchClear(suggestions)
  }

  onChange = e => {
    this.props.onChange(e)
    if (
      (isKeyboardAbTest() || isAutoActivateSearchTest()) &&
      e.target.value.length > 0 &&
      !this.state.locked
    ) {
      this.setState({ locked: true }) // locks keyboard for keyboard activation a/b test
    }
  }

  // checks for common search suggestion test and
  // sets common search suggestions
  setAbTestSuggestions = () => {
    if (
      isCommonSearchTest() &&
      this.props.search.length === 0 &&
      this.state.focused &&
      !this.props.searchResults.items
    ) {
      this.props.setCommonSuggestions(AbTestSuggestions)
    }
  }

  getAbTestFocus = () => {
    if (isKeyboardAbTest() || isAutoActivateSearchTest()) {
      return this.state.focused && this.state.locked
    }
    return this.state.focused
  }

  render() {
    let iFrame = inIframe()
    return (
      <SearchBubble
        {...this.props}
        focused={this.getAbTestFocus()}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onChange={this.onChange}
        cancelClick={this.cancelClick}
        resultClick={this.resultClick}
        searchClear={this.searchClear}
        setInputRef={i => (this.input = i)}
        iFrame={iFrame}
      />
    )
  }
}

const mapStateToProps = state => ({
  brandConfigs: state.brand,
  searchSuggestions: state.search.suggestions.items,
  activeSuggestion: state.search.suggestions.active,
  searchResults: state.search.results,
  message: state.search.message,
  search: state.search.search,
  loading: state.loading,
  finishedInputting: state.search.finished,
  suggestionsLoading: state.search.loading,
  submitted: state.search.submitted,
  identifiers: state.identifiers,
  editorActive: state.editor.isActive,
  forCovid19: state.showCovid19AlertBanner 
})

const mapDispatchToProps = (dispatch, ownProps) => ({
  setCommonSuggestions: suggestions => {
    dispatch(actions.setSearchSuggestions(suggestions))
  },
  onChange: e => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(actions.searchChange(e.target.value))
  },
  searchClear: suggestions => {
    dispatch(actions.searchChange("", suggestions))
  },
  submitSearch: query => {
    dispatch(actions.interpretSearch(query))
  },
  submitClarifier: (token, title) => {
    dispatch(actions.submitClarifier(token, title))
    /* Reset for next symptom search */
    searchBarClicked = false
  },
  submitRawQuery: () => {
    dispatch(actions.submitRawQuery())
  },
  startInterview: () => dispatch(actions.startInterview()),
  setInactive: () => dispatch(actions.setSearchActive(false)),
  searchFocus: () => {
    dispatch(actions.setSearchActive(true))
    if (!searchBarClicked) {
      tracker.searchClicked()
      searchBarClicked = true
    }
  },
  searchClick: () => dispatch(actions.searchFailure()),
  increment: () => {
    dispatch(actions.incrementActiveSuggestion())
  },
  decrement: () => {
    dispatch(actions.decrementActiveSuggestion())
  },
})

SearchBubbleContainer = connect(mapStateToProps, mapDispatchToProps)(
  mouseTrap(SearchBubbleContainer)
)

export { SearchBubbleContainer as default, SearchBubble }



// WEBPACK FOOTER //
// ./assets/search/components/SearchBubble.js